
import { Component, Vue, Prop } from "vue-property-decorator";
import { API_ROUTES } from "@/utils/API";
import { SMUser, SMCases } from "@/store";

import BackgroundImageLeft from "@/assets/images/report/bcg-left.png";
import BackgroundImageRight from "@/assets/images/report/bcg-right.png";
import BackgroundImageLeftPurple from "@/assets/images/report/bcg-left-purple.png";
import BackgroundImageRightPurple from "@/assets/images/report/bcg-right-purple.png";
import { API_TCase } from "@/models/cases";
import { TO_DO_TASK_STATUS } from "@/constants/tasks";
import {
  REPORT_CORRECTION_SERVICE_STATUS,
  REPORT_GENERATION_SERVICE_STATUS,
  SERVICE_STATUS,
} from "@/constants/services";

const MESSAGES = {
  statuses: {
    in_progress: {
      title: "The eligibility report will be generated\n" + "once all the tasks are completed\n",
      description:
        "The report will highlight:\n" +
        "Eligibility of the Candidate,\n" +
        "Approximate time required to get a Skilled Worker visa,\n" +
        "All the fees related to hiring migrant talent.\n",
    },
    report_correction: {
      title: "Generate a new report",
      description:
        "Once the details are changed (on 'Candidate details' and\n" +
        "'Company details' tabs) and all the tasks are completed, click on 'Generate report'\n" +
        "to see the eligibility check results.\n",
    },
    report_generation: {
      candidate: {
        title: "We are generating your report!",
        description:
          "We will inform you once the report is ready. You'll get access \n" +
          "to your eligibility report, fees associated with immigration\n" +
          "process and approximate timelines.\n",
      },
      employee: {
        title: "We are generating your report!",
        description:
          "We will inform you once the report is ready. You'll get access \n" +
          "to your eligibility report, fees associated with immigration\n" +
          "process and approximate timelines.\n",
      },
      employer: {
        title: "We are generating your report!",
        description:
          "We will inform you once the report is ready. You'll get access\n" +
          "to your candidate's eligibility, fees associated with immigration\n" +
          "process and approximate timelines.\n",
      },
    },
  },
  buttons: {
    generateReport: "Generate report",
    retriggerReportGeneration: "Regenerate report",
  },
  tooltip: "All the tasks should be\ncompleted to generate a\nreport.",
};

const REPORT_ALIAS = "ca_report_candidate_assessment";

const SUCCESS_HTTP_STATUS_CODE = 200;

@Component({
  components: {
    ActionButtons: () => import("@/components/ActionContainer/ActionButtons.vue"),
  },
})
export default class WidgetReport extends Vue {
  @Prop({ type: String, required: true }) readonly caseStatus!: string;

  messages = MESSAGES;

  statuses = SERVICE_STATUS;

  backgroundImageLeft = BackgroundImageLeft;
  backgroundImageRight = BackgroundImageRight;
  backgroundImageLeftPurple = BackgroundImageLeftPurple;
  backgroundImageRightPurple = BackgroundImageRightPurple;

  get computedTitle() {
    const { caseStatus } = this;

    const messages = this.messages.statuses;

    if (caseStatus === "report_generation") {
      console.log("SMUser.user.role", SMUser.user.role);
      return messages[caseStatus][SMUser.user.role]?.title;
    }

    return messages[caseStatus].title;
  }

  get computedDescription() {
    const { caseStatus } = this;

    const messages = this.messages.statuses;

    if (caseStatus === "report_generation") {
      console.log("SMUser.user.role", SMUser.user.role);
      return messages[caseStatus][SMUser.user.role]?.description;
    }

    return messages[caseStatus].description;
  }

  get status() {
    return SMCases.caseStatus || REPORT_GENERATION_SERVICE_STATUS;
  }

  get isReportCorrection() {
    return this.caseStatus === REPORT_CORRECTION_SERVICE_STATUS || SMUser.isExpert;
  }

  get isReportGenerated() {
    return this.caseStatus === REPORT_GENERATION_SERVICE_STATUS;
  }

  get caseId(): string {
    return this.$route.params.id;
  }

  get tasks() {
    return (SMCases.case as API_TCase).tasks;
  }

  get hasToDoTasks() {
    return !!this.tasks?.filter((task) => task.status === TO_DO_TASK_STATUS).length;
  }

  loading = false;

  get generateReportDisabled() {
    if (this.loading) return true;

    // DEV-9339. Separate logic for "Expert" role;
    if (SMUser.isExpert) return this.hasToDoTasks;

    return this.hasToDoTasks;
  }

  async generateReport() {
    try {
      this.loading = true;

      SMCases.setCaseStatus(REPORT_GENERATION_SERVICE_STATUS);

      const { caseId } = this;

      if (!caseId) return;

      const url = API_ROUTES.CASE_REPORT_GENERATE(caseId, REPORT_ALIAS);

      await this.axios.get(url);
    } catch ({ response }) {
      if ((response as Response).status !== SUCCESS_HTTP_STATUS_CODE) {
        console.warn(response);
      }
    } finally {
      this.loading = false;

      await this.getCase();
    }
  }

  get isExpert() {
    return SMUser.isExpert;
  }

  get isReportGenerationProcess() {
    return SMCases.isReportGenerationProcess && this.isExpert;
  }

  async retriggerReportGeneration() {
    try {
      const { caseId } = this;

      if (!caseId) return;

      this.loading = true;

      const url = API_ROUTES.CASE_REPORT_CHANGE(this.caseId);

      await this.axios.get(url);
    } finally {
      SMCases.setCaseStatus(REPORT_CORRECTION_SERVICE_STATUS);

      this.loading = false;
    }
  }

  init = false;

  async getCase() {
    try {
      const { data } = await this.axios.get(API_ROUTES.CASE_ID(this.caseId));

      if (!data) return;

      SMCases.setCaseStatus(data.status);

      SMCases.setCase(data);

      await this.$nextTick();

      if (SMCases.caseStatus !== REPORT_GENERATION_SERVICE_STATUS) return;
    } finally {
      this.init = true;
    }
  }

  mounted() {
    if (!this.init) this.getCase();
  }

  activated() {
    if (!this.init) this.getCase();
  }
}
